/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { authClientsDetachFromMcpServer } from "../funcs/authClientsDetachFromMcpServer.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AuthClient } from "../models/components/authclient.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DetachAuthClientFromMcpServerRequest,
  DetachAuthClientFromMcpServerSecurity,
} from "../models/operations/detachauthclientfrommcpserver.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DetachAuthClientFromMcpServerMutationVariables = {
  request: DetachAuthClientFromMcpServerRequest;
  security?: DetachAuthClientFromMcpServerSecurity | undefined;
  options?: RequestOptions;
};

export type DetachAuthClientFromMcpServerMutationData = AuthClient;

export type DetachAuthClientFromMcpServerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * detachAuthClientFromMcpServer authClients
 *
 * @remarks
 * Stop an MCP server offering an auth client in its typed auth configuration. The client and its other servers are unchanged. Takes effect only where typed auth client resolution is enforced. It revokes no upstream credentials and does not remove the server's existing upstream OAuth association, which keeps serving elsewhere, including in shadow mode. Requires org:admin.
 */
export function useDetachAuthClientFromMcpServerMutation(
  options?: MutationHookOptions<
    DetachAuthClientFromMcpServerMutationData,
    DetachAuthClientFromMcpServerMutationError,
    DetachAuthClientFromMcpServerMutationVariables
  >,
): UseMutationResult<
  DetachAuthClientFromMcpServerMutationData,
  DetachAuthClientFromMcpServerMutationError,
  DetachAuthClientFromMcpServerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDetachAuthClientFromMcpServerMutation(client, options),
    ...options,
  });
}

export function mutationKeyDetachAuthClientFromMcpServer(): MutationKey {
  return ["@gram/client", "authClients", "detachFromMcpServer"];
}

export function buildDetachAuthClientFromMcpServerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DetachAuthClientFromMcpServerMutationVariables,
  ) => Promise<DetachAuthClientFromMcpServerMutationData>;
} {
  return {
    mutationKey: mutationKeyDetachAuthClientFromMcpServer(),
    mutationFn: function detachAuthClientFromMcpServerMutationFn({
      request,
      security,
      options,
    }): Promise<DetachAuthClientFromMcpServerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(authClientsDetachFromMcpServer(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
